import { Package, Truck, Wrench, type LucideIcon } from 'lucide-react';
import { statusState } from 'pages/AdminIssues/components/upcomingAssignment';
import type { InterventionRequestStatus } from 'api/interventionRequestsApi';

/**
 * The mock-up's tints and type glyphs, as class lists. Markup-free so the list card and the detail
 * pill read the same colours — `ASSIGNMENT_TONE` stays the back office's, this one is his.
 */

/** The chosen day or pill — sky, as everywhere a technician picks. */
export const PICKED = 'border-sky-300 bg-sky-50 font-medium text-sky-800 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-200';

export type TintName = 'sky' | 'emerald' | 'rose' | 'violet' | 'amber' | 'zinc';

/** `bg` / `fg` / `dot` are the mock-up's own `--<tint>-bg` / `-fg` / `-dot`. */
export interface Tint {
  bg: string;
  fg: string;
  dot: string;
}

export const TINT: Record<TintName, Tint> = {
  sky: { bg: 'bg-sky-50 dark:bg-sky-950', fg: 'text-sky-700 dark:text-sky-400', dot: 'bg-sky-500' },
  emerald: {
    bg: 'bg-emerald-50 dark:bg-emerald-950',
    fg: 'text-emerald-700 dark:text-emerald-400',
    dot: 'bg-emerald-500',
  },
  rose: { bg: 'bg-rose-50 dark:bg-rose-950', fg: 'text-rose-700 dark:text-rose-400', dot: 'bg-rose-500' },
  violet: {
    bg: 'bg-violet-50 dark:bg-violet-950',
    fg: 'text-violet-700 dark:text-violet-400',
    dot: 'bg-violet-500',
  },
  amber: {
    bg: 'bg-amber-50 dark:bg-amber-950',
    fg: 'text-amber-700 dark:text-amber-400',
    dot: 'bg-amber-500',
  },
  zinc: { bg: 'bg-zinc-50 dark:bg-zinc-950', fg: 'text-zinc-700 dark:text-zinc-400', dot: 'bg-zinc-500' },
};

/** Sky for a new request, emerald accepted, rose refused — zinc for the states nobody is asked in. */
export const statusTint = (status: InterventionRequestStatus): Tint => {
  switch (statusState(status)) {
    case 'accepted':
      return TINT.emerald;
    case 'declined':
      return TINT.rose;
    case 'pending':
      return TINT.sky;
    default:
      return TINT.zinc;
  }
};

export interface RequestTypeVisual {
  tint: Tint;
  icon: LucideIcon;
}

/** Accents and case dropped: the types are free Jira strings, « Démontage » and « demontage » both. */
const fold = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/** Keyword families, in order — « mobilier » before « intervention », as the mock-up reads them. */
const TYPE_RULES: { keywords: string[]; visual: RequestTypeVisual }[] = [
  { keywords: ['installation'], visual: { tint: TINT.violet, icon: Wrench } },
  { keywords: ['deplacement', 'demontage', 'mobilier'], visual: { tint: TINT.sky, icon: Truck } },
  { keywords: ['sav', 'reparation', 'intervention'], visual: { tint: TINT.amber, icon: Wrench } },
  { keywords: ['envoi', 'manquant', 'reappro'], visual: { tint: TINT.emerald, icon: Package } },
];

// Neutral on purpose: an unmatched type is not a family, and a guessed glyph mislabels the mission.
const NEUTRAL_TYPE: RequestTypeVisual = { tint: TINT.zinc, icon: Wrench };

export const requestTypeVisual = (requestType: string | null): RequestTypeVisual => {
  if (!requestType) return NEUTRAL_TYPE;
  const folded = fold(requestType);
  return (
    TYPE_RULES.find((rule) => rule.keywords.some((keyword) => folded.includes(keyword)))?.visual ??
    NEUTRAL_TYPE
  );
};
